/**
 * WttGrid — thebigplan's TTBuilder working-timetable grid, uplifted for the
 * Studio's Baseline stage (Chris, 2026-08-20: "use this timetable layout from
 * working timetable for the Baseline view so it can be viewed and tuned the
 * way we build and tune timetables now").
 *
 * Faithful port of thebigplan `src/pages/ttbuilder/WorkingTimetable.tsx` +
 * `ttDerive.ts` (read-only cut): headcode band, Loco/Set rows, the 9
 * timing-point slots per direction with arr/dep chips and the Newtondale
 * request-halt row, crossing `x` markers, the Forms chain row, and the
 * Internal · exact / Public · rounded display modes.
 *
 * One deliberate addition: when `onTuneDep` is supplied, each working's ORIGIN
 * departure cell becomes editable — the one dimension the Studio engine
 * actually models (a working is origin→destination at a departure time).
 * Tuning writes to the DESIGN copy only; the baseline stays the railway's
 * real timetable.
 */
import { useState } from 'react'
import type { CSSProperties } from 'react'
import type { BaselinePattern, BaselineWorking } from './baseline'

// ---- thebigplan theme tokens (theme.ts, verbatim subset) -------------------
const C = {
  paperWarm: '#F5EBD3', paperSoft: '#FFFDF6', card: '#FFFFFF',
  ink: '#2B2014', inkSoft: '#604C32', inkMute: '#9F8B6B', inkFaint: '#C2B391',
  brassDk: '#7A540B', brassGlow: '#F4E2A8', brassBg: '#FBEFCB',
  sageDk: '#4F6852', signal: '#B83A2B', signalDk: '#8A2A1F',
  heather: '#7A5D8C',
  hairline: '#E8DCBC', hairlineLt: '#F2EAD3',
}
const F = {
  serif: "'Playfair Display', 'IBM Plex Serif', Georgia, serif",
  body: "'Nunito', 'IBM Plex Sans', 'Segoe UI', sans-serif",
  mono: "'IBM Plex Mono', ui-monospace, monospace",
}

// ---- ttDerive (thebigplan src/pages/ttbuilder/ttDerive.ts, verbatim) --------
export const ROWS: Record<'N' | 'S', [string, string][]> = {
  N: [['PIC', 'd'], ['LEV', 'a'], ['LEV', 'd'], ['NEW', 'f'], ['GOA', 'a'], ['GOA', 'd'], ['GRO', 'a'], ['GRO', 'd'], ['WHB', 'a']],
  S: [['WHB', 'd'], ['GRO', 'a'], ['GRO', 'd'], ['GOA', 'a'], ['GOA', 'd'], ['NEW', 'f'], ['LEV', 'a'], ['LEV', 'd'], ['PIC', 'a']],
}
const ST_NAME: Record<string, string> = {
  PIC: 'Pickering', LEV: 'Levisham', NEW: 'Newtondale', GOA: 'Goathland', GRO: 'Grosmont', WHB: 'Whitby',
}
const toMin = (s: string): number => {
  const d = s.replace(/[^0-9]/g, '').padStart(4, '0')
  return Number(d.slice(0, 2)) * 60 + Number(d.slice(2))
}
export interface Tok { t?: number | null; x?: boolean; flag?: string }
export function parseTok(tok: string | undefined | null): Tok {
  if (!tok) return { t: null }
  if (tok === 'r' || tok === 'ns' || tok === 'RR') return { flag: tok }
  const x = /x/i.test(tok)
  return { t: toMin(tok), x }
}
export const fmt = (m: number | null | undefined): string =>
  m == null ? '' : `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
const roundDep = (m: number): number => Math.ceil(m / 5) * 5
const roundArr = (m: number): number => Math.round(m / 5) * 5

/** Public · rounded derivation: departures round UP to 5, arrivals to nearest 5. */
const pubOf = (tok: Tok, kind: string): number | null =>
  tok.t == null ? null : kind === 'd' ? roundDep(tok.t) : roundArr(tok.t)

/** Tokens for one working: the raw 9-slot t_json when present, else synthesized
 *  from the dep-preferred `times` record (older API — degrades gracefully). */
export function tokensOf(w: BaselineWorking): string[] {
  if (w.t_json) {
    try {
      const arr = JSON.parse(w.t_json) as unknown[]
      if (Array.isArray(arr)) return arr.map((x) => String(x ?? ''))
    } catch { /* fall through */ }
  }
  const dir = w.direction === 'S' ? 'S' : 'N'
  return ROWS[dir].map(([st, kind]) => (kind === 'f' ? '' : (w.times[st] ?? '').replace(':', '')))
}

// ---- component --------------------------------------------------------------
export interface WttGridProps {
  workings: BaselineWorking[]
  pattern?: BaselinePattern | null
  season?: string | null
  /**
   * Tune hook: called with (workingId, 'HH:MM') when the planner edits a
   * working's ORIGIN departure. Writes to the DESIGN copy — the baseline
   * shown here is the railway's real timetable and never mutates.
   */
  onTuneDep?: (id: string, hhmm: string) => void
}

const tdBase: CSSProperties = {
  borderRight: `1px solid ${C.hairlineLt}`, borderBottom: `1px solid ${C.hairlineLt}`,
  textAlign: 'center', whiteSpace: 'nowrap', height: 30,
}
const labLeft: CSSProperties = {
  position: 'sticky', left: 0, zIndex: 2, background: C.paperSoft,
  borderRight: `2px solid ${C.hairline}`, borderBottom: `1px solid ${C.hairlineLt}`,
  padding: '0 12px', textAlign: 'left', whiteSpace: 'nowrap',
}

function KindChip({ kind }: { kind: string }) {
  if (kind === 'f') return <span style={{ fontFamily: F.mono, fontSize: 9.5, color: C.inkMute }}>req / pass</span>
  const isA = kind === 'a'
  return (
    <span style={{
      fontFamily: F.mono, fontSize: 9.5, fontWeight: 700, padding: '1px 5px', borderRadius: 4, marginLeft: 6,
      color: isA ? C.sageDk : C.brassDk, background: isA ? '#E4ECDF' : C.brassBg,
    }}>{kind}</span>
  )
}

function DirGrid({ dir, workings, mode, showAll, onTuneDep }: {
  dir: 'N' | 'S'
  workings: BaselineWorking[]
  mode: 'internal' | 'public'
  showAll: boolean
  onTuneDep?: (id: string, hhmm: string) => void
}) {
  const rows = ROWS[dir]
  const toks = workings.map((w) => tokensOf(w))
  const rowUsed = rows.map((_, i) => toks.some((t) => (t[i] ?? '').trim() !== ''))
  const visRows = rows.map((r, i) => ({ r, i })).filter(({ i }) => showAll || rowUsed[i])
  // Origin dep slot index = the first 'd' row this working has a time in.
  const originIdx = toks.map((t) => rows.findIndex(([, kind], i) => kind === 'd' && (t[i] ?? '').trim() !== '' && !parseTok(t[i]).flag))

  if (workings.length === 0) return null
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontFamily: F.body, fontSize: 12.5, fontWeight: 800, color: C.brassDk, margin: '10px 0 5px' }}>
        {dir === 'N' ? '→ Northbound · up to Whitby' : '← Southbound · down to Pickering'}
        <span style={{ fontWeight: 500, color: C.inkMute, marginLeft: 8 }}>{workings.length} workings</span>
      </div>
      <div style={{ overflowX: 'auto', border: `1px solid ${C.hairline}`, borderRadius: 12, background: C.card }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ ...labLeft, background: C.brassDk, color: C.brassGlow, fontFamily: F.body, fontSize: 10, fontWeight: 800, letterSpacing: 1, height: 34, borderBottom: 'none' }}>HEADCODE</th>
              {workings.map((w) => (
                <th key={w.id} style={{ ...tdBase, background: C.brassDk, borderRight: '1px solid rgba(255,255,255,0.12)', borderBottom: 'none', height: 34, minWidth: 74 }}>
                  <span style={{ fontFamily: F.mono, fontSize: 13.5, fontWeight: 800, color: '#FFF8E6' }}>{w.headcode}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {([
              ['Loco', (w: BaselineWorking) => w.diagram ?? w.traction ?? '—'],
              ['Set', (w: BaselineWorking) => w.set ?? '—'],
            ] as const).map(([label, pick]) => (
              <tr key={label} style={{ background: C.paperWarm }}>
                <td style={{ ...labLeft, background: C.paperWarm, fontFamily: F.body, fontSize: 11, fontWeight: 800, color: C.inkSoft }}>{label}</td>
                {workings.map((w) => (
                  <td key={w.id} style={tdBase}>
                    <span style={{ fontFamily: F.mono, fontSize: 11.5, fontWeight: 600, color: C.ink }}>{pick(w)}</span>
                  </td>
                ))}
              </tr>
            ))}

            {visRows.map(({ r: [st, kind], i }, vi) => {
              const prevSt = vi > 0 ? visRows[vi - 1].r[0] : null
              return (
                <tr key={`${st}.${kind}.${i}`}>
                  <td style={labLeft}>
                    <span style={{ fontFamily: F.body, fontSize: 12.5, fontWeight: 700, color: prevSt === st ? 'transparent' : C.ink }}>
                      {ST_NAME[st]}
                      {st === 'NEW' && <span style={{ fontFamily: F.mono, fontSize: 9, color: C.inkMute, marginLeft: 4 }}>halt</span>}
                    </span>
                    <KindChip kind={kind} />
                  </td>
                  {workings.map((w, wi) => {
                    const raw = parseTok(toks[wi][i])
                    if (kind === 'f') {
                      return (
                        <td key={w.id} style={tdBase}>
                          <span style={{ fontFamily: F.mono, fontSize: 11, fontWeight: 700, color: raw.flag === 'r' ? C.sageDk : raw.flag === 'ns' ? C.signalDk : C.inkFaint }}>
                            {raw.flag ?? '·'}
                          </span>
                        </td>
                      )
                    }
                    const isOrigin = onTuneDep && i === originIdx[wi]
                    if (isOrigin) {
                      return (
                        <td key={w.id} style={{ ...tdBase, background: '#FDF8EA' }} title="Origin departure — edit to tune the DESIGN copy (the baseline itself never changes)">
                          <input
                            type="time"
                            defaultValue={raw.t != null ? fmt(raw.t) : ''}
                            onBlur={(e) => { if (e.target.value && e.target.value !== fmt(raw.t)) onTuneDep(w.id, e.target.value) }}
                            style={{ fontFamily: F.mono, fontSize: 12, fontWeight: 700, color: C.brassDk, border: `1px dashed ${C.brassDk}`, borderRadius: 4, background: 'transparent', width: 76, textAlign: 'center' }}
                          />
                        </td>
                      )
                    }
                    const showT = mode === 'public' ? pubOf(raw, kind) : raw.t
                    return (
                      <td key={w.id} title={raw.x ? 'Crossing / passing time' : undefined}
                        style={{ ...tdBase, background: raw.x && mode === 'internal' ? '#FBF1D8' : 'transparent' }}>
                        {showT != null ? (
                          <span style={{ fontFamily: F.mono, fontSize: 12.5, fontWeight: raw.x ? 700 : 600, color: raw.x && mode === 'internal' ? C.brassDk : C.ink }}>
                            {fmt(showT)}{raw.x && mode === 'internal' ? <span style={{ color: C.signal, fontWeight: 800 }}>x</span> : ''}
                          </span>
                        ) : <span style={{ color: C.inkFaint, fontSize: 12 }}>·</span>}
                      </td>
                    )
                  })}
                </tr>
              )
            })}

            <tr style={{ background: C.paperWarm }}>
              <td style={{ ...labLeft, background: C.paperWarm, fontFamily: F.body, fontSize: 11, fontWeight: 800, color: C.inkSoft }}>Forms</td>
              {workings.map((w) => (
                <td key={w.id} style={tdBase}>
                  <span style={{ fontFamily: F.mono, fontSize: 11.5, fontWeight: 600, color: C.ink }}>{w.forms ?? '—'}</span>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function WttGrid({ workings, pattern, season, onTuneDep }: WttGridProps) {
  const [mode, setMode] = useState<'internal' | 'public'>('internal')
  const [showAll, setShowAll] = useState(false)
  const north = workings.filter((w) => w.direction !== 'S')
  const south = workings.filter((w) => w.direction === 'S')
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
        {pattern && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 11, height: 11, borderRadius: 3, background: pattern.colour ?? C.brassDk, display: 'inline-block' }} />
            <span style={{ fontFamily: F.serif, fontSize: 18, fontWeight: 700, color: C.ink }}>{pattern.name}</span>
            <span style={{ fontFamily: F.mono, fontSize: 10.5, fontWeight: 700, color: C.brassDk, background: C.brassBg, padding: '2px 7px', borderRadius: 6 }}>{pattern.code}</span>
            {season && <span style={{ fontFamily: F.serif, fontStyle: 'italic', fontSize: 12.5, color: C.inkMute }}>{season}</span>}
          </span>
        )}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <span style={{ display: 'inline-flex', border: `1px solid ${C.hairline}`, borderRadius: 999, overflow: 'hidden' }}>
            {([['internal', 'Internal · exact'], ['public', 'Public · rounded']] as const).map(([k, l]) => (
              <button key={k} type="button" onClick={() => setMode(k)}
                style={{ border: 'none', padding: '4px 12px', cursor: 'pointer', fontFamily: F.body, fontSize: 12, fontWeight: 700, background: mode === k ? C.brassDk : C.paperSoft, color: mode === k ? '#FFF8E6' : C.inkSoft }}>
                {l}
              </button>
            ))}
          </span>
          <button type="button" onClick={() => setShowAll((v) => !v)}
            style={{ border: `1px solid ${C.hairline}`, borderRadius: 999, padding: '4px 12px', cursor: 'pointer', fontFamily: F.body, fontSize: 12, fontWeight: 700, background: showAll ? C.brassBg : C.paperSoft, color: C.inkSoft }}>
            {showAll ? 'Hide unused timing points' : 'Show all timing points'}
          </button>
        </span>
      </div>
      {mode === 'public' && (
        <div style={{ background: '#ECE0F2', border: '1px solid rgba(122,93,140,0.25)', borderRadius: 8, padding: '6px 10px', fontFamily: F.body, fontSize: 11.5, color: C.heather, marginBottom: 6 }}>
          Public view — auto-derived: departures round up to 5 min, crossings hidden.
        </div>
      )}
      {onTuneDep && (
        <div style={{ fontFamily: F.body, fontSize: 11.5, color: C.inkMute, marginBottom: 4 }}>
          The dashed origin-departure cells are editable — a change tunes your <strong>Design</strong> copy of this
          timetable (the baseline here stays the railway&apos;s real one). Everything else about a working is tuned on
          the Design stage.
        </div>
      )}
      <DirGrid dir="N" workings={north} mode={mode} showAll={showAll} onTuneDep={onTuneDep} />
      <DirGrid dir="S" workings={south} mode={mode} showAll={showAll} onTuneDep={onTuneDep} />
    </div>
  )
}
